import { forwardRef, type InputHTMLAttributes, type ReactNode } from "react";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
	sizeVariant?: "default" | "large";
	label?: ReactNode;
	error?: string;
	required?: boolean;
	isDark?: boolean;
}

const sizeVariantLabelStyles: Record<
	NonNullable<InputProps["sizeVariant"]>,
	string
> = {
	default: "mb-2 text-sm",
	large: "text-base",
};

const sizeVariantInputStyles: Record<
	NonNullable<InputProps["sizeVariant"]>,
	string
> = {
	default: "py-2 px-4",
	large: "py-3 px-5",
};

const Input = forwardRef<HTMLInputElement, InputProps>(
	(
		{
			sizeVariant = "default",
			label,
			error,
			id,
			className = "",
			required,
			isDark = false,
			...props
		},
		ref,
	) => {
		const inputId = id || props.name;

		return (
			<div>
				{label && (
					<label
						htmlFor={inputId}
						className={`block font-medium text-t-primary font-body ${sizeVariantLabelStyles[sizeVariant]} ${isDark ? "text-white" : "text-t-primary"}`}
						style={{
							fontFamily: "var(--font-body)",
							fontWeight: "var(--font-body-weight)",
						}}
					>
						{label}
						{required && <span className="text-red-500"> *</span>}
					</label>
				)}
				<input
					ref={ref}
					id={inputId}
					className={`w-full ${isDark ? "bg-gray-900 border-gray-800 text-white" : "bg-white border-gray-200 text-t-primary"} border rounded-lg shadow-sm font-sans text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed placeholder:text-gray-400 placeholder:font-light text-t-primary ${
						error ? "border-red-500" : ""
					} ${sizeVariantInputStyles[sizeVariant]} ${className}`}
					{...props}
				/>
				{error && <p className="text-sm text-red-500 mt-1">{error}</p>}
			</div>
		);
	},
);

Input.displayName = "Input";

export default Input;
